import { useCallback } from 'react';
import {
  Rack,
  Server,
  LocationData,
  GameNotification,
  PendingDelivery,
} from '../types';
import { RACK_TYPES, SERVER_TYPES, TOR_SWITCH_TYPES, MPO_PANEL_TYPES, MGMT_SWITCH_TYPES, CONTRACT_TYPES, POWER_GRIDS, DELIVERY_DAYS_BY_TIER } from '../constants';
import { calculateLocationGrossPower } from '../utils/powerCalculations';

interface UseRackManagementProps {
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  triggerNotification: (message: string, type: GameNotification['type']) => void;
  currentFloor: string | null;
  getFloorCoolingEfficiency?: (floorId: string, locationId: string) => number;
  gameTime?: number;
  addPendingDelivery?: (d: PendingDelivery) => void;
  technicianCount?: number;
}

interface RackManagementHook {
  buyRack: (locationId: string, rackId: number | null, rackTypeId: string) => boolean;
  removeRack: (locationId: string, rackId: number) => boolean;
  installServer: (locationId: string, rackId: number | null, serverTypeId: string) => boolean;
  fillRack: (locationId: string, rackId: number, serverTypeId: string) => number;
  removeServer: (locationId: string, rackId: number, serverId: string) => boolean;
  installTORSwitch: (locationId: string, rackId: number | null, switchTypeId: string) => boolean;
  installMpoPatchPanel: (locationId: string, rackId: number | null, panelTypeId: string) => boolean;
  upgradeMpoPatchPanel: (locationId: string, rackId: number | null, panelTypeId: string) => boolean;
  installRedundantTorSwitch: (locationId: string, rackId: number | null, switchTypeId: string) => boolean;
  installManagementSwitch: (locationId: string, rackId: number | null, switchTypeId: string) => boolean;
}

export const useRackManagement = ({
  locations,
  setLocations,
  money,
  setMoney,
  triggerNotification,
  currentFloor,
  getFloorCoolingEfficiency = () => 1.0,
  gameTime = 0,
  addPendingDelivery,
  technicianCount = 0,
}: UseRackManagementProps): RackManagementHook => {
  
  // Function to buy and place a rack
  const buyRack = useCallback((locationId: string, rackId: number | null, rackTypeId: string) => {
    if (!locations[locationId] || rackId === null) return false;
    
    // Ensure we have a valid floor
    if (!currentFloor) {
      triggerNotification('No floor selected', 'error');
      return false;
    }
    
    // Ensure the floor exists in the location
    if (!locations[locationId].floors || !locations[locationId].floors[currentFloor]) {
      triggerNotification('Selected floor not found', 'error');
      return false;
    }
    
    const rackType = RACK_TYPES.find(r => r.id === rackTypeId);
    if (!rackType) return false;
    
    // Check if we have enough money
    if (money < rackType.cost) {
      triggerNotification('Not enough money to purchase this rack', 'error');
      return false;
    }
    
    // Check if the location has a power grid connection
    if (!locations[locationId].powerGridId) {
      triggerNotification('You need to connect to a power grid first', 'warning');
      return false;
    }
    
    // DATACENTER POWER CHAIN VALIDATION
    // 1. Check if the location has a transformer installed
    if (!locations[locationId].transformer) {
      triggerNotification('You need to install a transformer first', 'warning');
      return false;
    }

    // 2. Check if the location has a powerhouse installed
    if (!locations[locationId].powerhouse?.installed) {
      triggerNotification('You need to install a powerhouse first', 'warning');
      return false;
    }

    // 3. Check if the location has a UPS installed
    if (!locations[locationId].ups?.installed) {
      triggerNotification('You need to install a UPS first', 'warning');
      return false;
    }

    // 4. Check if a power supplier is selected
    if (!locations[locationId].selectedPowerSupplierId) {
      triggerNotification('You need to select a power supplier first', 'warning');
      return false;
    }
    
    // 5. Check if there's enough available power capacity for this rack type
    const requiredRackPower = 0.1; // kW minimal rack overhead
    const availablePower = locations[locationId].availablePowerKW;
    if (availablePower < requiredRackPower) {
      triggerNotification(`Not enough power capacity. Need ${requiredRackPower.toFixed(1)}kW but only ${availablePower.toFixed(1)}kW available`, 'error');
      return false;
    }
    
    // Check if there's any cooling capacity installed
    if (!locations[locationId].totalCoolingCapacityKW || locations[locationId].totalCoolingCapacityKW <= 0) {
      triggerNotification('You need a complete cooling chain first (Heat Rejection → Chiller → CRAH)', 'warning');
      return false;
    }
    
    // Check if there's enough available cooling capacity (apply floor efficiency)
    const floorCoolingEfficiency = getFloorCoolingEfficiency(currentFloor, locationId);
    const availableCooling = locations[locationId].totalCoolingCapacityKW - (locations[locationId].usedCoolingCapacityKW || 0);
    const requiredRackCoolingRaw = 0.1; // kW minimal rack overhead
    const requiredRackCooling = requiredRackCoolingRaw / floorCoolingEfficiency;
    if (availableCooling < requiredRackCooling) {
      triggerNotification(`Not enough cooling capacity. Need ${requiredRackCooling.toFixed(1)}kW but only ${availableCooling.toFixed(1)}kW available`, 'error');
      return false;
    }
    
    setMoney(prev => prev - rackType.cost);
    
    // Update the location with the new rack
    setLocations(prev => {
      const updatedLocation = { ...prev[locationId] };
      
      // Create updated floors object
      const updatedFloors = { ...updatedLocation.floors };
      
      // Create updated floor object
      const updatedFloor = { ...updatedFloors[currentFloor] };
      
      // Create updated racks array for the floor
      const updatedFloorRacks = [...updatedFloor.racks];
      
      // Find the rack by ID and update it
      const rackIndex = updatedFloorRacks.findIndex(r => r.id === rackId);
      if (rackIndex === -1) return prev;
      
      // Create the updated rack object
      const updatedRack = {
        ...updatedFloorRacks[rackIndex],
        type: rackTypeId,
        maxCapacity: rackType.capacity,
        powerDraw: requiredRackPower,
        coolingNeeded: requiredRackCoolingRaw,
        usedPower: 0,
        usedCooling: 0,
        usedCapacity: 0,
        usedU: 0,
      };
      
      // Update the rack in the floor's racks array
      updatedFloorRacks[rackIndex] = updatedRack;
      updatedFloor.racks = updatedFloorRacks;
      
      // Update floor's usedPower to include the rack's power
      updatedFloor.usedPower = updatedFloorRacks.reduce(
        (total: number, rack: Rack) => total + (rack.type ? rack.powerDraw : 0) + 
          rack.servers.reduce((sum: number, server: Server) => sum + server.powerDraw, 0),
        0
      );
      
      // Update floor's usedCooling with cooling efficiency bonus
      updatedFloor.usedCooling = updatedFloorRacks.reduce(
        (total: number, rack: Rack) => {
          // Apply cooling efficiency to rack cooling needs
          const rackCooling = rack.type ? rack.coolingNeeded / floorCoolingEfficiency : 0;
          // Apply cooling efficiency to server cooling needs
          const serversCooling = rack.servers.reduce(
            (sum: number, server: Server) => sum + server.coolingNeeded / floorCoolingEfficiency, 0
          );
          return total + rackCooling + serversCooling;
        },
        0
      );
      
      // Update the floor in the floors object
      updatedFloors[currentFloor] = updatedFloor;
      
      // Update the floors in the location
      updatedLocation.floors = updatedFloors;
      
      // Incrementally update power and cooling usage instead of recalculating from scratch
      // This ensures we don't lose power/cooling usage from other sources like chillers
      updatedLocation.usedPower += requiredRackPower;
      updatedLocation.usedCoolingCapacityKW = (updatedLocation.usedCoolingCapacityKW || 0) + requiredRackCooling;
      
      // Calculate the gross power (total capacity) for the updated location
      const grossPower = calculateLocationGrossPower(updatedLocation, POWER_GRIDS);
      updatedLocation.grossPowerKW = grossPower;
      
      // Update available power (gross power minus used power)
      updatedLocation.availablePowerKW = grossPower - updatedLocation.usedPower;
      
      // Trigger a notification of success
      triggerNotification(`${rackType.name} installed successfully!`, 'success');
      
      return {
        ...prev,
        [locationId]: updatedLocation
      };
    });

    return true;
  }, [locations, money, setMoney, setLocations, triggerNotification, currentFloor, getFloorCoolingEfficiency]);

  // Function to install a server in a rack
  const installServer = useCallback((locationId: string, rackId: number | null, serverTypeId: string): boolean => {
    if (rackId === null) return false;
    
    // Ensure we have a valid floor
    if (!currentFloor) {
      triggerNotification('No floor selected', 'error');
      return false;
    }
    
    // Ensure the floor exists in the location
    if (!locations[locationId].floors || !locations[locationId].floors[currentFloor]) {
      triggerNotification('Selected floor not found', 'error');
      return false;
    }
    
    const serverType = SERVER_TYPES.find(s => s.id === serverTypeId);
    if (!serverType) return false;
    
    if (money < serverType.cost) {
      triggerNotification('Not enough money to install this server', 'error');
      return false;
    }
    
    // Get the current location data for validation
    const location = locations[locationId];
    if (!location) {
      triggerNotification('Location not found', 'error');
      return false;
    }
    
    // Get the current floor
    const floor = location.floors[currentFloor];
    if (!floor) {
      triggerNotification('Floor not found', 'error');
      return false;
    }
    
    // Find the rack in the floor's racks array
    const rack = floor.racks.find(r => r.id === rackId);
    if (!rack || !rack.type) {
      triggerNotification('Rack not found or not initialized', 'error');
      return false;
    }
    
    // Check if rack has capacity
    const rackType = RACK_TYPES.find(rt => rt.id === rack.type);
    if (!rackType) {
      triggerNotification('Rack type not found', 'error');
      return false;
    }
    
    // Check TOR switch requirement
    if (!rack.torSwitch) {
      triggerNotification('Install a TOR switch in this rack before adding servers', 'warning');
      return false;
    }

    // Check TOR switch port capacity (one port per server)
    const switchType = TOR_SWITCH_TYPES.find(s => s.id === rack.torSwitch!.typeId);
    if (switchType && rack.servers.length >= switchType.portCount) {
      triggerNotification(`TOR switch is out of ports (${switchType.portCount} max). Install a larger switch to add more servers.`, 'warning');
      return false;
    }

    // Check U-height capacity — only U1–U43 are server-usable (U44–U48 reserved for MPO/TOR/airflow)
    const RACK_SERVER_MAX_U = 43;
    const usedU = rack.servers.reduce((sum, s) => {
      const st = SERVER_TYPES.find(t => t.id === s.type);
      return sum + (st?.uSize ?? 1);
    }, 0);
    if (usedU + serverType.uSize > RACK_SERVER_MAX_U) {
      triggerNotification(`Not enough rack units. Need ${serverType.uSize}U but only ${RACK_SERVER_MAX_U - usedU}U free`, 'warning');
      return false;
    }
    
    // Check if there's any cooling capacity installed
    if (!location.totalCoolingCapacityKW || location.totalCoolingCapacityKW <= 0) {
      triggerNotification('You need a complete cooling chain first (Heat Rejection → Chiller → CRAH)', 'warning');
      return false;
    }
    
    // Apply floor efficiency (efficiency reduces cooling needed)
    const floorCoolingEfficiency = getFloorCoolingEfficiency(currentFloor, locationId);
    const serverCoolingNeededRaw = serverType.coolingNeeded;
    const effectiveServerCoolingNeeded = serverCoolingNeededRaw / floorCoolingEfficiency;
    
    // Check if there's enough cooling capacity
    const availableCooling = location.totalCoolingCapacityKW - (location.usedCoolingCapacityKW || 0);
    if (availableCooling < effectiveServerCoolingNeeded) {
      triggerNotification(`Not enough cooling capacity. Need ${effectiveServerCoolingNeeded.toFixed(1)}kW but only ${availableCooling.toFixed(1)}kW available`, 'error');
      return false;
    }
    
    // Check rack-level power budget (PDU rating)
    const rackPowerUsed = (rack.usedPower || 0) + (rack.powerDraw || 0);
    const rackPowerBudget = rackType.power;
    if (rackPowerUsed + serverType.powerDraw > rackPowerBudget) {
      const remaining = Math.max(0, rackPowerBudget - rackPowerUsed);
      triggerNotification(`Rack PDU limit reached. Budget: ${rackPowerBudget} kW, used: ${rackPowerUsed.toFixed(2)} kW, remaining: ${remaining.toFixed(2)} kW`, 'error');
      return false;
    }

    // Check if there's enough location-wide power capacity
    const availablePower = location.availablePowerKW;
    if (availablePower < serverType.powerDraw) {
      triggerNotification(`Not enough power capacity. Need ${serverType.powerDraw.toFixed(1)}kW but only ${availablePower.toFixed(1)}kW available`, 'error');
      return false;
    }
    
    // If we've passed all validation checks, now we can proceed with installation

    // For higher-tier servers, queue a delivery instead of instant install
    const baseDays = DELIVERY_DAYS_BY_TIER[serverType.unlockLevel] ?? 0;
    const deliveryDays = Math.max(0, baseDays - technicianCount);
    if (deliveryDays > 0 && addPendingDelivery) {
      setMoney(prev => prev - serverType.cost);
      const absoluteDay = Math.floor(gameTime / 1440);
      addPendingDelivery({
        id: `delivery-${Date.now()}`,
        serverTypeId,
        rackId: rackId!,
        floorId: currentFloor,
        locationId,
        deliveryAtDay: absoluteDay + deliveryDays,
        orderedAtDay: absoluteDay,
        cost: serverType.cost,
      });
      triggerNotification(
        `${serverType.name} ordered — arrives in ${deliveryDays} day${deliveryDays > 1 ? 's' : ''}.`,
        'info',
      );
      return true;
    }

    // Create a new server — customer count is driven by the server's contract category
    const contract = CONTRACT_TYPES.find(c => c.id === serverType.contractCategory);
    const newServer: Server = {
      id: Date.now().toString(),
      type: serverTypeId,
      status: 'online',
      powerDraw: serverType.powerDraw,
      coolingNeeded: serverCoolingNeededRaw, // store raw; floor efficiency applied in aggregates
      revenue: serverType.revenue,
      customers: contract ? contract.customersPerServer : serverType.customers,
      failureStatus: 'operational',
      satisfactionScore: 100,
      installedAt: gameTime,
      ageStage: 'new',
      ageEfficiencyMultiplier: 1.0,
      utilizationRate: 0.75,
    };
    
    // Update the location state with the new server
    setLocations(prev => {
      // Re-fetch from prev to ensure we have the latest state
      const location = prev[locationId];
      if (!location) return prev;
      
      // Create a new location object
      const updatedLocation = { ...location };
      
      // Create updated floors object
      const updatedFloors = { ...updatedLocation.floors };
      
      // Create updated floor object
      const updatedFloor = { ...updatedFloors[currentFloor] };
      
      // Create updated racks array for the floor
      const updatedFloorRacks = [...updatedFloor.racks];
      
      // Find the index of the rack to update
      const rackIndex = updatedFloorRacks.findIndex(r => r.id === rackId);
      if (rackIndex === -1) return prev;
      
      // Create a new rack object with the updated servers array
      const updatedRack = { ...updatedFloorRacks[rackIndex] };
      updatedRack.servers = [...updatedRack.servers, newServer];
      
      // Update the rack's used power, cooling, and U-height
      updatedRack.usedPower = (updatedRack.usedPower || 0) + serverType.powerDraw;
      updatedRack.usedCooling = (updatedRack.usedCooling || 0) + effectiveServerCoolingNeeded;
      updatedRack.usedU = (updatedRack.usedU || 0) + serverType.uSize;
      
      // Update the rack in the floor's racks array
      updatedFloorRacks[rackIndex] = updatedRack;
      updatedFloor.racks = updatedFloorRacks;
      
      // Update floor's usedPower to include the server's power
      updatedFloor.usedPower = updatedFloorRacks.reduce(
        (total: number, rack: Rack) => total + (rack.type ? rack.powerDraw : 0) + 
          rack.servers.reduce((sum: number, server: Server) => sum + server.powerDraw, 0),
        0
      );
      
      // Update floor's usedCooling with cooling efficiency bonus
      updatedFloor.usedCooling = updatedFloorRacks.reduce(
        (total: number, rack: Rack) => {
          // Apply cooling efficiency to rack cooling needs
          const rackCooling = rack.type ? rack.coolingNeeded / floorCoolingEfficiency : 0;
          // Apply cooling efficiency to server cooling needs
          const serversCooling = rack.servers.reduce(
            (sum: number, server: Server) => sum + server.coolingNeeded / floorCoolingEfficiency, 0
          );
          return total + rackCooling + serversCooling;
        },
        0
      );
      
      // Update the floor in the floors object
      updatedFloors[currentFloor] = updatedFloor;
      
      // Update the floors in the location
      updatedLocation.floors = updatedFloors;
      
      // Update the location's used cooling capacity
      updatedLocation.usedCoolingCapacityKW = (updatedLocation.usedCoolingCapacityKW || 0) + effectiveServerCoolingNeeded;
      
      // Incrementally update power usage
      updatedLocation.usedPower += serverType.powerDraw;
      
      // Calculate the gross power (total capacity) for the updated location
      const grossPower = calculateLocationGrossPower(updatedLocation, POWER_GRIDS);
      updatedLocation.grossPowerKW = grossPower;
      
      // Update available power (gross power minus used power)
      updatedLocation.availablePowerKW = grossPower - updatedLocation.usedPower;
      
      return {
        ...prev,
        [locationId]: updatedLocation
      };
    });
    
    // Deduct the cost - safe to do now after all validations have passed
    setMoney(prev => prev - serverType.cost);
    
    // Trigger a notification
    triggerNotification(`Installed ${serverType.name}`, 'success');

    return true;
  }, [locations, money, setMoney, setLocations, triggerNotification, currentFloor, getFloorCoolingEfficiency, gameTime, addPendingDelivery, technicianCount]);

  const installTORSwitch = useCallback((locationId: string, rackId: number | null, switchTypeId: string): boolean => {
    if (rackId === null) return false;
    if (!currentFloor) { triggerNotification('No floor selected', 'error'); return false; }

    const switchType = TOR_SWITCH_TYPES.find(s => s.id === switchTypeId);
    if (!switchType) return false;

    const location = locations[locationId];
    if (!location) return false;

    const floor = location.floors[currentFloor];
    if (!floor) return false;

    const rack = floor.racks.find(r => r.id === rackId);
    if (!rack || !rack.type) {
      triggerNotification('Rack not found or not purchased yet', 'error');
      return false;
    }

    if (!rack.mpoPatchPanel) {
      triggerNotification('Install an MPO patch panel (U48) before adding a TOR switch', 'warning');
      return false;
    }

    if (rack.torSwitch) {
      triggerNotification('This rack already has a primary TOR switch installed', 'warning');
      return false;
    }

    // Verify MPO panel supports the switch uplink speed
    const mpoPanel = MPO_PANEL_TYPES.find(p => p.id === rack.mpoPatchPanel!.typeId);
    if (mpoPanel && mpoPanel.maxUplinkGbps < switchType.uplinkBandwidthGbps) {
      triggerNotification(`MPO panel only supports up to ${mpoPanel.maxUplinkGbps}G. Upgrade to a higher-density panel first.`, 'warning');
      return false;
    }

    if (money < switchType.cost) {
      triggerNotification('Not enough money to purchase this TOR switch', 'error');
      return false;
    }

    // Check rack PDU budget for switch power draw
    const rackType = RACK_TYPES.find(rt => rt.id === rack.type);
    if (!rackType) return false;
    const rackPowerUsed = (rack.usedPower || 0) + (rack.powerDraw || 0);
    if (rackPowerUsed + switchType.powerDrawKW > rackType.power) {
      triggerNotification(`Not enough rack PDU budget for this switch (needs ${switchType.powerDrawKW} kW)`, 'error');
      return false;
    }

    setMoney(prev => prev - switchType.cost);

    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      const updatedLoc = { ...loc };
      const updatedFloors = { ...updatedLoc.floors };
      const updatedFloor = { ...updatedFloors[currentFloor] };
      const updatedRacks = [...updatedFloor.racks];
      const rackIdx = updatedRacks.findIndex(r => r.id === rackId);
      if (rackIdx === -1) return prev;

      const updatedRack = { ...updatedRacks[rackIdx] };
      updatedRack.torSwitch = { typeId: switchTypeId, installedAt: Date.now() };
      updatedRack.usedPower = (updatedRack.usedPower || 0) + switchType.powerDrawKW;
      updatedRacks[rackIdx] = updatedRack;
      updatedFloor.racks = updatedRacks;
      updatedFloors[currentFloor] = updatedFloor;
      updatedLoc.floors = updatedFloors;
      updatedLoc.usedPower = (updatedLoc.usedPower || 0) + switchType.powerDrawKW;

      const grossPower = calculateLocationGrossPower(updatedLoc, POWER_GRIDS);
      updatedLoc.grossPowerKW = grossPower;
      updatedLoc.availablePowerKW = grossPower - updatedLoc.usedPower;

      return { ...prev, [locationId]: updatedLoc };
    });

    triggerNotification(`${switchType.name} installed in rack`, 'success');
    return true;
  }, [locations, money, setMoney, setLocations, triggerNotification, currentFloor]);

  // Install MPO patch panel at U48 — prerequisite for all TOR switches
  const installMpoPatchPanel = useCallback((locationId: string, rackId: number | null, panelTypeId: string): boolean => {
    if (rackId === null) return false;
    if (!currentFloor) { triggerNotification('No floor selected', 'error'); return false; }

    const panelType = MPO_PANEL_TYPES.find(p => p.id === panelTypeId);
    if (!panelType) return false;

    const location = locations[locationId];
    if (!location) return false;
    const floor = location.floors[currentFloor];
    if (!floor) return false;
    const rack = floor.racks.find(r => r.id === rackId);
    if (!rack || !rack.type) { triggerNotification('Rack not purchased yet', 'error'); return false; }

    if (rack.mpoPatchPanel) {
      triggerNotification('This rack already has an MPO patch panel installed', 'warning');
      return false;
    }
    if (money < panelType.cost) {
      triggerNotification('Not enough money for this MPO patch panel', 'error');
      return false;
    }

    setMoney(prev => prev - panelType.cost);
    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      const updatedFloors = { ...loc.floors };
      const updatedFloor = { ...updatedFloors[currentFloor] };
      const updatedRacks = [...updatedFloor.racks];
      const idx = updatedRacks.findIndex(r => r.id === rackId);
      if (idx === -1) return prev;
      updatedRacks[idx] = { ...updatedRacks[idx], mpoPatchPanel: { typeId: panelTypeId, installedAt: Date.now() } };
      updatedFloor.racks = updatedRacks;
      updatedFloors[currentFloor] = updatedFloor;
      return { ...prev, [locationId]: { ...loc, floors: updatedFloors } };
    });

    triggerNotification(`${panelType.name} installed at U48`, 'success');
    return true;
  }, [locations, money, setMoney, setLocations, triggerNotification, currentFloor]);

  // Upgrade an existing MPO panel to a higher tier (charges the price delta).
  const upgradeMpoPatchPanel = useCallback((locationId: string, rackId: number | null, panelTypeId: string): boolean => {
    if (rackId === null) return false;
    if (!currentFloor) { triggerNotification('No floor selected', 'error'); return false; }

    const newPanel = MPO_PANEL_TYPES.find(p => p.id === panelTypeId);
    if (!newPanel) return false;

    const location = locations[locationId];
    if (!location) return false;
    const floor = location.floors[currentFloor];
    if (!floor) return false;
    const rack = floor.racks.find(r => r.id === rackId);
    if (!rack || !rack.type) { triggerNotification('Rack not purchased yet', 'error'); return false; }
    if (!rack.mpoPatchPanel) { triggerNotification('No MPO panel installed to upgrade', 'warning'); return false; }

    const currentPanel = MPO_PANEL_TYPES.find(p => p.id === rack.mpoPatchPanel!.typeId);
    if (!currentPanel) return false;
    if (newPanel.maxUplinkGbps <= currentPanel.maxUplinkGbps) {
      triggerNotification('Select a higher-tier MPO panel to upgrade.', 'warning');
      return false;
    }

    const upgradeCost = Math.max(0, newPanel.cost - currentPanel.cost);
    if (money < upgradeCost) {
      triggerNotification(`Not enough money. Upgrade costs $${upgradeCost.toLocaleString()}.`, 'error');
      return false;
    }

    setMoney(prev => prev - upgradeCost);
    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      const updatedFloors = { ...loc.floors };
      const updatedFloor = { ...updatedFloors[currentFloor] };
      const updatedRacks = [...updatedFloor.racks];
      const idx = updatedRacks.findIndex(r => r.id === rackId);
      if (idx === -1) return prev;
      updatedRacks[idx] = { ...updatedRacks[idx], mpoPatchPanel: { typeId: panelTypeId, installedAt: Date.now() } };
      updatedFloor.racks = updatedRacks;
      updatedFloors[currentFloor] = updatedFloor;
      return { ...prev, [locationId]: { ...loc, floors: updatedFloors } };
    });

    triggerNotification(`MPO panel upgraded to ${newPanel.name} (${newPanel.maxUplinkGbps}G max uplink).`, 'success');
    return true;
  }, [locations, money, setMoney, setLocations, triggerNotification, currentFloor]);

  // Install out-of-band management switch at U44 — enables remote (iDRAC/iLO) management
  const installManagementSwitch = useCallback((locationId: string, rackId: number | null, switchTypeId: string): boolean => {
    if (rackId === null) return false;
    if (!currentFloor) { triggerNotification('No floor selected', 'error'); return false; }

    const switchType = MGMT_SWITCH_TYPES.find(s => s.id === switchTypeId);
    if (!switchType) return false;

    const location = locations[locationId];
    if (!location) return false;
    const floor = location.floors[currentFloor];
    if (!floor) return false;
    const rack = floor.racks.find(r => r.id === rackId);
    if (!rack || !rack.type) { triggerNotification('Rack not purchased yet', 'error'); return false; }

    if (rack.managementSwitch) {
      triggerNotification('This rack already has a management switch installed', 'warning');
      return false;
    }
    if (money < switchType.cost) {
      triggerNotification('Not enough money for this management switch', 'error');
      return false;
    }

    const rackType = RACK_TYPES.find(rt => rt.id === rack.type);
    if (!rackType) return false;
    const rackPowerUsed = (rack.usedPower || 0) + (rack.powerDraw || 0);
    if (rackPowerUsed + switchType.powerDrawKW > rackType.power) {
      triggerNotification(`Not enough rack PDU budget for this switch (needs ${switchType.powerDrawKW} kW)`, 'error');
      return false;
    }

    setMoney(prev => prev - switchType.cost);
    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      const updatedFloors = { ...loc.floors };
      const updatedFloor = { ...updatedFloors[currentFloor] };
      const updatedRacks = [...updatedFloor.racks];
      const idx = updatedRacks.findIndex(r => r.id === rackId);
      if (idx === -1) return prev;
      const updatedRack = { ...updatedRacks[idx] };
      updatedRack.managementSwitch = { typeId: switchTypeId, installedAt: Date.now() };
      updatedRack.usedPower = (updatedRack.usedPower || 0) + switchType.powerDrawKW;
      updatedRacks[idx] = updatedRack;
      updatedFloor.racks = updatedRacks;
      updatedFloors[currentFloor] = updatedFloor;
      const updatedLoc = { ...loc, floors: updatedFloors };
      updatedLoc.usedPower = (updatedLoc.usedPower || 0) + switchType.powerDrawKW;
      const grossPower = calculateLocationGrossPower(updatedLoc, POWER_GRIDS);
      updatedLoc.grossPowerKW = grossPower;
      updatedLoc.availablePowerKW = grossPower - updatedLoc.usedPower;
      return { ...prev, [locationId]: updatedLoc };
    });

    triggerNotification(`${switchType.name} installed at U44 — remote management enabled for this rack.`, 'success');
    return true;
  }, [locations, money, setMoney, setLocations, triggerNotification, currentFloor]);

  // Install redundant (secondary) TOR switch at U45 — requires primary TOR first
  const installRedundantTorSwitch = useCallback((locationId: string, rackId: number | null, switchTypeId: string): boolean => {
    if (rackId === null) return false;
    if (!currentFloor) { triggerNotification('No floor selected', 'error'); return false; }

    const switchType = TOR_SWITCH_TYPES.find(s => s.id === switchTypeId);
    if (!switchType) return false;

    const location = locations[locationId];
    if (!location) return false;
    const floor = location.floors[currentFloor];
    if (!floor) return false;
    const rack = floor.racks.find(r => r.id === rackId);
    if (!rack || !rack.type) { triggerNotification('Rack not purchased yet', 'error'); return false; }

    if (!rack.torSwitch) {
      triggerNotification('Install a primary TOR switch (U46) first', 'warning');
      return false;
    }
    if (rack.torSwitchRedundant) {
      triggerNotification('This rack already has a redundant TOR switch', 'warning');
      return false;
    }
    if (money < switchType.cost) {
      triggerNotification('Not enough money for this TOR switch', 'error');
      return false;
    }

    const rackType = RACK_TYPES.find(rt => rt.id === rack.type);
    if (!rackType) return false;
    const rackPowerUsed = (rack.usedPower || 0) + (rack.powerDraw || 0);
    if (rackPowerUsed + switchType.powerDrawKW > rackType.power) {
      triggerNotification(`Not enough rack PDU budget (needs ${switchType.powerDrawKW} kW)`, 'error');
      return false;
    }

    setMoney(prev => prev - switchType.cost);
    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      const updatedFloors = { ...loc.floors };
      const updatedFloor = { ...updatedFloors[currentFloor] };
      const updatedRacks = [...updatedFloor.racks];
      const idx = updatedRacks.findIndex(r => r.id === rackId);
      if (idx === -1) return prev;
      const updatedRack = { ...updatedRacks[idx] };
      updatedRack.torSwitchRedundant = { typeId: switchTypeId, installedAt: Date.now() };
      updatedRack.usedPower = (updatedRack.usedPower || 0) + switchType.powerDrawKW;
      updatedRacks[idx] = updatedRack;
      updatedFloor.racks = updatedRacks;
      updatedFloors[currentFloor] = updatedFloor;
      const updatedLoc = { ...loc, floors: updatedFloors };
      updatedLoc.usedPower = (updatedLoc.usedPower || 0) + switchType.powerDrawKW;
      const grossPower = calculateLocationGrossPower(updatedLoc, POWER_GRIDS);
      updatedLoc.grossPowerKW = grossPower;
      updatedLoc.availablePowerKW = grossPower - updatedLoc.usedPower;
      return { ...prev, [locationId]: updatedLoc };
    });

    triggerNotification(`${switchType.name} installed at U45 (MLAG redundancy active)`, 'success');
    return true;
  }, [locations, money, setMoney, setLocations, triggerNotification, currentFloor]);

  // Remove (decommission) a server from a rack, refunding 50% of its cost
  const removeServer = useCallback((locationId: string, rackId: number, serverId: string): boolean => {
    if (!currentFloor) return false;

    const location = locations[locationId];
    if (!location) return false;
    const floor = location.floors?.[currentFloor];
    if (!floor) return false;
    const rack = floor.racks.find(r => r.id === rackId);
    if (!rack) return false;
    const server = rack.servers.find(s => s.id === serverId);
    if (!server) return false;

    const serverType = SERVER_TYPES.find(s => s.id === server.type);
    if (!serverType) return false;

    const salvage = Math.floor(serverType.cost * 0.5);
    const floorCoolingEfficiency = getFloorCoolingEfficiency
      ? getFloorCoolingEfficiency(currentFloor, locationId)
      : 1;
    const effectiveCooling = serverType.coolingNeeded / floorCoolingEfficiency;

    setMoney(prev => prev + salvage);

    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      const fl = { ...loc.floors[currentFloor] };
      const racks = fl.racks.map(r => {
        if (r.id !== rackId) return r;
        const updatedRack = {
          ...r,
          servers: r.servers.filter(s => s.id !== serverId),
          usedPower: Math.max(0, (r.usedPower || 0) - serverType.powerDraw),
          usedCooling: Math.max(0, (r.usedCooling || 0) - effectiveCooling),
          usedU: Math.max(0, (r.usedU || 0) - serverType.uSize),
        };
        return updatedRack;
      });
      fl.racks = racks;

      const updatedFloors = { ...loc.floors, [currentFloor]: fl };
      const updatedLoc = {
        ...loc,
        floors: updatedFloors,
        usedPower: Math.max(0, loc.usedPower - serverType.powerDraw),
        usedCoolingCapacityKW: Math.max(0, (loc.usedCoolingCapacityKW || 0) - effectiveCooling),
      };
      const grossPower = calculateLocationGrossPower(updatedLoc, POWER_GRIDS);
      updatedLoc.grossPowerKW = grossPower;
      updatedLoc.availablePowerKW = grossPower - updatedLoc.usedPower;

      return { ...prev, [locationId]: updatedLoc };
    });

    triggerNotification(`${serverType.name} decommissioned — $${salvage.toLocaleString()} salvage value recovered`, 'success');
    return true;
  }, [locations, setMoney, setLocations, triggerNotification, currentFloor, getFloorCoolingEfficiency]);

  // Remove an empty rack, refunding 40% of its cost
  const removeRack = useCallback((locationId: string, rackId: number): boolean => {
    if (!currentFloor) return false;
    const location = locations[locationId];
    if (!location) return false;
    const floor = location.floors?.[currentFloor];
    if (!floor) return false;
    const rack = floor.racks.find(r => r.id === rackId);
    if (!rack || !rack.type) return false;
    if ((rack.servers ?? []).length > 0) {
      triggerNotification('Remove all servers before selling the rack.', 'error');
      return false;
    }

    const rackType = RACK_TYPES.find(t => t.id === rack.type);
    const salvage = rackType ? Math.floor(rackType.cost * 0.4) : 0;

    // Power freed by TOR/MPO switches
    const torPower = rack.torSwitch
      ? (TOR_SWITCH_TYPES.find(t => t.id === rack.torSwitch!.typeId)?.powerDrawKW ?? 0) : 0;
    const redundantTorPower = rack.torSwitchRedundant
      ? (TOR_SWITCH_TYPES.find(t => t.id === rack.torSwitchRedundant!.typeId)?.powerDrawKW ?? 0) : 0;
    const mgmtPower = rack.managementSwitch
      ? (MGMT_SWITCH_TYPES.find(t => t.id === rack.managementSwitch!.typeId)?.powerDrawKW ?? 0) : 0;
    const switchPower = torPower + redundantTorPower + mgmtPower;

    setMoney(prev => prev + salvage);

    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      const fl = { ...loc.floors[currentFloor] };
      fl.racks = fl.racks.map(r =>
        r.id !== rackId ? r : { ...r, type: null, mpoPatchPanel: undefined, torSwitch: undefined, torSwitchRedundant: undefined, managementSwitch: undefined, servers: [], usedPower: 0, usedCooling: 0, usedU: 0 }
      );
      const updatedFloors = { ...loc.floors, [currentFloor]: fl };
      const updatedLoc = {
        ...loc,
        floors: updatedFloors,
        usedPower: Math.max(0, loc.usedPower - switchPower),
      };
      const grossPower = calculateLocationGrossPower(updatedLoc, POWER_GRIDS);
      updatedLoc.grossPowerKW = grossPower;
      updatedLoc.availablePowerKW = grossPower - updatedLoc.usedPower;
      return { ...prev, [locationId]: updatedLoc };
    });

    triggerNotification(`Rack sold — $${salvage.toLocaleString()} recovered (40% of purchase price).`, 'success');
    return true;
  }, [locations, setMoney, setLocations, triggerNotification, currentFloor]);

  // Fill a rack with as many of a given server type as will fit (power, U, ports, money, cooling)
  const fillRack = useCallback((locationId: string, rackId: number, serverTypeId: string): number => {
    if (!currentFloor) return 0;
    const location = locations[locationId];
    if (!location) return 0;
    const floor = location.floors[currentFloor];
    if (!floor) return 0;
    const rack = floor.racks.find(r => r.id === rackId);
    if (!rack || !rack.type || !rack.torSwitch) return 0;
    const serverType = SERVER_TYPES.find(s => s.id === serverTypeId);
    if (!serverType) return 0;
    const rackType = RACK_TYPES.find(rt => rt.id === rack.type);
    if (!rackType) return 0;
    const switchType = TOR_SWITCH_TYPES.find(s => s.id === rack.torSwitch!.typeId);
    if (!switchType) return 0;
    const floorCoolingEfficiency = getFloorCoolingEfficiency(currentFloor, locationId);
    const effectiveCooling = serverType.coolingNeeded / floorCoolingEfficiency;

    // Compute how many fit given current constraints
    const usedU = rack.servers.reduce((s, sv) => {
      const st = SERVER_TYPES.find(t => t.id === sv.type);
      return s + (st?.uSize ?? 1);
    }, 0);
    const freeU = 43 - usedU; // 43 = RACK_SERVER_MAX_U (U1-U43; U44-U48 reserved for infra)
    const freePorts = Math.max(0, switchType.portCount - rack.servers.length);
    const rackPowerUsed = (rack.usedPower || 0) + (rack.powerDraw || 0);
    const rackPowerFree = Math.max(0, rackType.power - rackPowerUsed);
    const locationPowerFree = Math.max(0, location.availablePowerKW);
    const coolingFree = Math.max(0, location.totalCoolingCapacityKW - (location.usedCoolingCapacityKW || 0));

    const byU = serverType.uSize > 0 ? Math.floor(freeU / serverType.uSize) : 0;
    const byPorts = freePorts;
    const byRackPower = serverType.powerDraw > 0 ? Math.floor(rackPowerFree / serverType.powerDraw) : 0;
    const byLocPower = serverType.powerDraw > 0 ? Math.floor(locationPowerFree / serverType.powerDraw) : 0;
    const byCooling = effectiveCooling > 0 ? Math.floor(coolingFree / effectiveCooling) : 0;
    const byMoney = serverType.cost > 0 ? Math.floor(money / serverType.cost) : 0;

    const count = Math.min(byU, byPorts, byRackPower, byLocPower, byCooling, byMoney);
    if (count <= 0) {
      triggerNotification('No room to install more servers (check power, cooling, U-space, ports, or funds)', 'warning');
      return 0;
    }

    const contract = CONTRACT_TYPES.find(c => c.id === serverType.contractCategory);
    const now = Date.now();

    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      const fl = { ...loc.floors[currentFloor] };
      const racks = [...fl.racks];
      const ri = racks.findIndex(r => r.id === rackId);
      if (ri === -1) return prev;
      const updatedRack = { ...racks[ri] };
      const newServers = Array.from({ length: count }, (_, i) => ({
        id: `${now + i}`,
        type: serverTypeId,
        status: 'online' as const,
        powerDraw: serverType.powerDraw,
        coolingNeeded: serverType.coolingNeeded,
        revenue: serverType.revenue,
        customers: contract ? contract.customersPerServer : serverType.customers,
        failureStatus: 'operational' as const,
        satisfactionScore: 100,
        installedAt: gameTime,
        ageStage: 'new' as const,
        ageEfficiencyMultiplier: 1.0,
        utilizationRate: 0.75,
      }));
      updatedRack.servers = [...updatedRack.servers, ...newServers];
      updatedRack.usedPower = (updatedRack.usedPower || 0) + serverType.powerDraw * count;
      updatedRack.usedCooling = (updatedRack.usedCooling || 0) + effectiveCooling * count;
      updatedRack.usedU = (updatedRack.usedU || 0) + serverType.uSize * count;
      racks[ri] = updatedRack;
      fl.racks = racks;
      fl.usedPower = racks.reduce((t, r) => t + (r.type ? r.powerDraw : 0) + r.servers.reduce((s, sv) => s + sv.powerDraw, 0), 0);
      fl.usedCooling = racks.reduce((t, r) => {
        const rc = r.type ? r.coolingNeeded / floorCoolingEfficiency : 0;
        const sc = r.servers.reduce((s, sv) => s + sv.coolingNeeded / floorCoolingEfficiency, 0);
        return t + rc + sc;
      }, 0);
      const updatedLoc = {
        ...loc,
        floors: { ...loc.floors, [currentFloor]: fl },
        usedCoolingCapacityKW: (loc.usedCoolingCapacityKW || 0) + effectiveCooling * count,
        usedPower: loc.usedPower + serverType.powerDraw * count,
      };
      const grossPower = calculateLocationGrossPower(updatedLoc, POWER_GRIDS);
      updatedLoc.grossPowerKW = grossPower;
      updatedLoc.availablePowerKW = grossPower - updatedLoc.usedPower;
      return { ...prev, [locationId]: updatedLoc };
    });

    setMoney(prev => prev - serverType.cost * count);
    triggerNotification(`Installed ${count}× ${serverType.name}`, 'success');
    return count;
  }, [locations, money, setMoney, setLocations, triggerNotification, currentFloor, getFloorCoolingEfficiency, gameTime]);

  return {
    buyRack,
    removeRack,
    installServer,
    fillRack,
    removeServer,
    installTORSwitch,
    installMpoPatchPanel,
    upgradeMpoPatchPanel,
    installRedundantTorSwitch,
    installManagementSwitch,
  };
};
